import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import { useAppConfig } from "../../../context/AppConfigContext";
import { handleApiError } from "../../../utils/handleApiError";

const SupplyItemFormModal = ({ open, onClose, onSave, initialData }) => {
  const [form, setForm] = useState({
    name: "",
    department: "",
    supplier: "",
    storePathIndex: 999,
    measurementUnit: "",
    isActive: true,
  });
  const [errors, setErrors] = useState({});

  const { config } = useAppConfig();
  const snackbar = useSnackbarAlert();
  const [suppliers, setSuppliers] = useState([]);
  const unitOptions = ["g", "kg", "lb", "bag", "unit", "carton", "case", "pcs"];

  useEffect(() => {
    if (initialData) {
      setForm({
        _id: initialData._id,
        name: initialData.name ?? "",
        department: initialData.department ?? "",
        supplier: initialData.supplier?._id,
        storePathIndex: initialData.storePathIndex ?? 999,
        measurementUnit: initialData.measurementUnit ?? "",
        isActive: initialData.isActive ?? true,
      });
    } else {
      // when adding a new item
      setForm({
        name: "",
        department: "",
        supplier: "",
        storePathIndex: 999,
        measurementUnit: "",
        isActive: true,
      });
    }
  }, [initialData]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await api.get("/admin/suppliers");
        setSuppliers(res.data);
      } catch (err) {
        console.error("Failed to load suppliers", err);
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    setErrors({});
  }, [open]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.department) newErrors.department = "Department is required";
    if (!form.supplier) newErrors.supplier = "Supplier is required";
    if (form.storePathIndex === null || form.storePathIndex === "")
      newErrors.storePathIndex = "Path index is required";
    if (!form.measurementUnit) newErrors.measurementUnit = "Unit is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (form._id) {
        await api.put(`/admin/supply-items/${form._id}`, form);
        snackbar.success("Item updated.");
      } else {
        await api.post("/admin/supply-items", form);
        snackbar.success("Item created.");
      }

      onSave();
      onClose();
    } catch (err) {
      handleApiError(err, snackbar, "Could not save item.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form._id ? "Edit" : "Add"} Supply Item</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "24px !important",
        }}>
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
          fullWidth
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          select
          label="Department"
          value={form.department}
          onChange={(e) => handleChange("department", e.target.value)}
          fullWidth
          error={!!errors.department}
          helperText={errors.department}>
          {config?.supplyDepartments?.map((dept) => (
            <MenuItem key={dept} value={dept}>
              {dept}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Supplier"
          value={form.supplier}
          onChange={(e) => handleChange("supplier", e.target.value)}
          fullWidth
          error={!!errors.supplier}
          helperText={errors.supplier}>
          {suppliers.map((s) => {
            return (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          label="In-store Path Index"
          type="number"
          value={form.storePathIndex}
          onChange={(e) =>
            handleChange("storePathIndex", Number(e.target.value))
          }
          fullWidth
          error={!!errors.storePathIndex}
          helperText={errors.storePathIndex}
        />
        <TextField
          select
          label="Unit"
          value={form.measurementUnit || ""}
          onChange={(e) => handleChange("measurementUnit", e.target.value)}
          fullWidth
          error={!!errors.measurementUnit}
          helperText={errors.measurementUnit}>
          {unitOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch
              checked={form.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplyItemFormModal;
